import { yupResolver } from '@hookform/resolvers/yup';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { CircularProgress } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { ErrorMessage } from 'components';
import { useToast } from 'components/Providers/ToastProvider';
import { useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { emailSchema } from 'schemas/emailValidationSchema';
import useOtpStore from 'store/useOtpStore';
import { ROUTES } from 'util/Constants';
import { HelloMuiLoadingButton } from 'components/Mui';
import CustomOutlinedInput from '../CustomOutlinedInput';

interface FormData {
    email: string;
}

const EnterEmail = () => {
    const { showMessage } = useToast();
    const { sendOtp } = useOtpStore();
    const [searchParams] = useSearchParams();
    const emailFromUrl = searchParams.get('email');

    const { handleSubmit, control, formState, reset } = useForm<FormData>({
        resolver: yupResolver<FormData>(emailSchema),
        defaultValues: {
            email: emailFromUrl || '',
        },
    });

    // Update form value when emailFromUrl changes
    useEffect(() => {
        if (emailFromUrl) {
            reset({ email: emailFromUrl });
        }
    }, [emailFromUrl, reset]);

    const sendOtpMutation = useMutation({
        mutationFn: sendOtp,
        onSuccess: (status) => {
            if (status === 201) {
                showMessage('Success', 'Enter your OTP, check your email box and spam folder!');
            }
        },
        onError: (error) => {
            showMessage('Error Occurred', error.message);
        },
    });

    const onEnterOtpClicked: SubmitHandler<FormData> = ({ email }) => sendOtpMutation.mutate(email);

    return (
        <form onSubmit={handleSubmit(onEnterOtpClicked)}>
            <div className="loginDialog bg-white rounded-[20px] md:px-[36px] sm:px-[24px] px-[16px] py-[48px] flex flex-col gap-[25px]">
                <div className="flex flex-col gap-[10px]">
                    <div className="text-black font-semibold text-[20px] leading-[21px]">Log in to your account</div>
                    <div className="text-my-black text-[14px] opacity-80 leading-[24px] ">
                        Dive back into hello:here! We've been busy gathering fresh real estate listings and market data – it's all waiting for you to explore.
                    </div>
                </div>
                <div>
                    <Controller
                        name="email"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <CustomOutlinedInput
                                name="email"
                                value={value}
                                onChange={onChange}
                                autoFocus={true}
                                placeholder="Email Address"
                                startIcon={<EmailOutlinedIcon />}
                                data-cy={`email-input`}
                            />
                        )}
                    />
                    {formState.errors.email && <ErrorMessage msg={`${formState.errors.email.message}`} data-cy={`email-error`} />}
                </div>
                <HelloMuiLoadingButton
                    loading={sendOtpMutation.isPending}
                    type="submit"
                    data-cy="submit-button"
                    variant="contained"
                    loadingIndicator={<CircularProgress color="primary" size={24} />}
                    sx={{
                        backgroundColor: '#3176bc',
                        '&:hover': {
                            backgroundColor: '#2563a8',
                        },
                    }}
                >
                    <div className="text-[14px]">
                        <span>Get your </span>
                        <span className="font-semibold">{'one time'} </span>
                        <span>password</span>
                    </div>
                </HelloMuiLoadingButton>
                <div className="text-center text-[14px]">
                    <span>Don't have an account? </span>
                    <Link className="text-my-blue font-bold" to={ROUTES.SIGN_UP}>
                        Sign up here
                    </Link>
                </div>
            </div>
        </form>
    );
};

export default EnterEmail;
